// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/TimelineBuilder.tsx
// ============================================================

/**
 * TimelineBuilder Component — M5 Split Hero
 *
 * Visual treatment: Light action panel (left) + dark timeline
 * preview (right) with gradient left accent bar.
 *
 * Flow:
 *   start  →  "Generate timeline" (click)  →  building  →  ready
 *   start  →  AI generates timeline         →  ready
 *
 * One confident primary action. No intermediate confirm step.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Timeline,
  TimelineEvent,
  TimelineOutput,
  TimelineBuilderProps,
  CaseType,
  TimelineCaseData,
  SUGGESTED_EVENTS_BY_CASE_TYPE,
} from "./types";
import {
  generateEventId,
  createEmptyEvent,
  sortTimelineEvents,
  validateTimelineEvents,
  createEventsFromSuggestions,
} from "./utils";
import TimelineEventCard from "./TimelineEventCard";
import TimelineOutputDisplay from "./TimelineOutput";
import SuggestionPanel from "./SuggestionPanel";
import { generateSuggestions, EventTemplate } from "./suggestionEngine";

// ── Display labels ──────────────────────────────────────────

const CASE_TYPE_LABELS: Record<CaseType, string> = {
  IMAGING: "Imaging",
  PROCEDURE: "Procedure",
  SURGERY: "Surgery",
  MEDICATION: "Medication",
  LEVEL_OF_CARE: "Level of Care",
  DME: "DME / Equipment",
  THERAPY: "Therapy",
  OTHER: "Other",
};

const CASE_TYPE_PILL_LABELS: Record<CaseType, string> = {
  IMAGING: "Imaging",
  PROCEDURE: "Procedure",
  SURGERY: "Surgery",
  MEDICATION: "Medication",
  LEVEL_OF_CARE: "Level of Care",
  DME: "DME",
  THERAPY: "Therapy",
  OTHER: "Other",
};

// ── Request type detection ──────────────────────────────────

interface DetectionResult {
  type: CaseType;
  confidence: "high" | "low";
}

const DETECTION_RULES: { keywords: string[]; type: CaseType; weight: number }[] = [
  {
    keywords: [
      "mri", "ct scan", "x-ray", "xray", "ultrasound", "imaging",
      "radiograph", "pet scan", "dexa", "mammogram", "arthrogram",
    ],
    type: "IMAGING",
    weight: 3,
  },
  {
    keywords: [
      "surgery", "surgical", "arthroplasty", "fusion", "laminectomy",
      "discectomy", "arthroscopy", "excision", "resection", "implant",
      "reconstruction", "replacement",
    ],
    type: "SURGERY",
    weight: 3,
  },
  {
    keywords: [
      "procedure", "injection", "nerve block", "epidural", "ablation",
      "biopsy", "catheterization", "endoscopy", "colonoscopy", "stent",
      "spinal cord stimulator", "trigger point",
    ],
    type: "PROCEDURE",
    weight: 3,
  },
  {
    keywords: [
      "medication", "drug", "rx", "prescription", "mg", "tablet",
      "capsule", "step therapy", "formulary", "prior authorization for",
      "biologic", "specialty medication", "infusion",
    ],
    type: "MEDICATION",
    weight: 2,
  },
  {
    keywords: [
      "admission", "inpatient", "observation", "skilled nursing",
      "rehab facility", "transfer", "icu", "level of care",
      "acute care", "discharge", "length of stay",
    ],
    type: "LEVEL_OF_CARE",
    weight: 3,
  },
  {
    keywords: [
      "wheelchair", "cpap", "bipap", "brace", "prosthetic", "orthotic",
      "dme", "durable medical equipment", "walker", "hospital bed",
      "oxygen", "nebulizer",
    ],
    type: "DME",
    weight: 3,
  },
  {
    keywords: [
      "physical therapy", "occupational therapy", "speech therapy",
      "therapy sessions", "rehabilitation", "pt sessions", "ot sessions",
      "behavioral health", "aba therapy",
    ],
    type: "THERAPY",
    weight: 2,
  },
];

function detectRequestType(
  caseData: TimelineCaseData | undefined,
  fallback: CaseType
): DetectionResult {
  if (!caseData) {
    return { type: fallback, confidence: fallback !== "OTHER" ? "high" : "low" };
  }

  const corpus = [
    caseData.diagnoses || "",
    caseData.request_type || "",
    caseData.request_details || "",
    caseData.denial_reason || "",
    ...(caseData.tried_treatments || []),
  ]
    .join(" ")
    .toLowerCase();

  if (!corpus.trim()) {
    return { type: fallback, confidence: fallback !== "OTHER" ? "high" : "low" };
  }

  const scores: Record<string, number> = {};
  for (const rule of DETECTION_RULES) {
    let hits = 0;
    for (const kw of rule.keywords) {
      if (corpus.includes(kw)) hits++;
    }
    if (hits > 0) {
      scores[rule.type] = (scores[rule.type] || 0) + hits * rule.weight;
    }
  }

  const entries = Object.entries(scores).sort((a, b) => b[1] - a[1]);

  if (entries.length === 0) {
    return { type: fallback, confidence: fallback !== "OTHER" ? "high" : "low" };
  }

  const [topType, topScore] = entries[0];
  const secondScore = entries.length > 1 ? entries[1][1] : 0;
  const isHigh = topScore >= 3 && topScore >= secondScore * 1.5;

  return {
    type: topType as CaseType,
    confidence: isHigh ? "high" : "low",
  };
}

// ── Treatment classifiers ───────────────────────────────────

const MEDICATION_KEYWORDS = [
  "medication", "drug", "rx", "mg", "tablet", "capsule", "injection",
  "ibuprofen", "naproxen", "acetaminophen", "tylenol", "advil", "motrin",
  "gabapentin", "lyrica", "pregabalin", "neurontin",
  "prednisone", "methylprednisolone", "steroid", "corticosteroid",
  "tramadol", "hydrocodone", "oxycodone", "morphine", "opioid",
  "cyclobenzaprine", "flexeril", "muscle relaxant", "baclofen",
  "meloxicam", "celebrex", "nsaid", "anti-inflammatory",
  "duloxetine", "cymbalta", "amitriptyline", "nortriptyline",
];

const THERAPY_KEYWORDS = [
  "physical therapy", "pt", "occupational therapy", "ot",
  "chiropractic", "acupuncture", "massage", "exercise",
  "home exercise", "hep", "stretching", "strengthening",
  "activity modification", "rest", "ice", "heat",
  "brace", "splint", "tens", "traction",
];

function classifyTreatment(treatment: string): "MEDICATION" | "CONSERVATIVE_THERAPY" {
  const lower = treatment.toLowerCase();
  for (const keyword of MEDICATION_KEYWORDS) {
    if (lower.includes(keyword)) return "MEDICATION";
  }
  for (const keyword of THERAPY_KEYWORDS) {
    if (lower.includes(keyword)) return "CONSERVATIVE_THERAPY";
  }
  return "CONSERVATIVE_THERAPY";
}

// ── Case data → events ──────────────────────────────────────

function createEventsFromCaseData(
  caseData: TimelineCaseData,
  caseType: CaseType
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  if (caseData.diagnoses?.trim()) {
    events.push({
      id: generateEventId(),
      event_type: "DIAGNOSIS",
      date_precision: "approximate",
      title: "Diagnosis",
      details: caseData.diagnoses.substring(0, 240),
      isNew: false,
    });
  }

  if (caseData.tried_treatments && caseData.tried_treatments.length > 0) {
    caseData.tried_treatments.forEach((treatment) => {
      if (!treatment?.trim()) return;
      const eventType = classifyTreatment(treatment);
      events.push({
        id: generateEventId(),
        event_type: eventType,
        date_precision: "approximate",
        title: eventType === "MEDICATION" ? `Tried: ${treatment}` : `Failed: ${treatment}`,
        details: "Did not provide adequate relief",
        outcome: "NO_CHANGE",
        isNew: false,
      });
    });
  }

  if (caseData.denial_reason?.trim()) {
    events.push({
      id: generateEventId(),
      event_type: "DENIAL",
      date_precision: "approximate",
      title: "Prior Authorization Denied",
      details: caseData.denial_reason.substring(0, 240),
      evidence_flags: ["DENIAL_LETTER"],
      isNew: false,
    });
  }

  if (caseData.request_type?.trim()) {
    events.push({
      id: generateEventId(),
      event_type: "REQUEST",
      date_precision: "exact",
      title: `Current Request: ${caseData.request_type}`,
      details: caseData.request_details?.substring(0, 240),
      outcome: "PENDING",
      isNew: false,
    });
  }

  return events;
}

function mergeEventsWithSuggestions(
  caseEvents: TimelineEvent[],
  suggestions: TimelineEvent[]
): TimelineEvent[] {
  const coveredTypes = new Set(caseEvents.map((e) => e.event_type));
  const filteredSuggestions = suggestions.filter(
    (s) =>
      !coveredTypes.has(s.event_type) ||
      s.event_type === "SYMPTOM" ||
      s.event_type === "FUNCTION_CHANGE"
  );
  return [...caseEvents, ...filteredSuggestions];
}

// ── Small UI components ─────────────────────────────────────

const TimelineIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SparkIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
  </svg>
);

const PencilIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
  </svg>
);

const ChevronIcon = ({ className = "w-4 h-4", direction = "down" }: { className?: string; direction?: "down" | "up" | "left" }) => {
  const paths: Record<string, string> = {
    down: "M19 9l-7 7-7-7",
    up: "M5 15l7-7 7 7",
    left: "M15 19l-7-7 7-7",
  };
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={paths[direction]} />
    </svg>
  );
};

const InfoTooltip = ({ text }: { text: string }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex ml-1">
      <button
        type="button"
        aria-label={text}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        className="text-slate-400 hover:text-slate-500 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-xs text-white bg-slate-800 rounded-md shadow-lg whitespace-nowrap z-10">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-800" />
        </span>
      )}
    </span>
  );
};

const Spinner = ({ className = "w-4 h-4" }: { className?: string }) => (
  <div
    className={`${className} animate-spin rounded-full border-2 border-current border-t-transparent`}
    role="status"
    aria-label="Loading"
  />
);

/**
 * Dark timeline preview SVG for the right panel.
 * Vertical rail with 4 labeled nodes (glowing teal bookends,
 * open slate midpoints) and a gradient rail. Illustrates
 * the timeline concept without mimicking wireframes.
 */
const DarkTimelinePreview = ({ visible }: { visible: boolean }) => (
  <svg
    className="absolute inset-0 w-full h-full"
    viewBox="0 0 220 240"
    preserveAspectRatio="xMidYMid meet"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.8s ease-out',
    }}
  >
    <defs>
      <radialGradient id="glowTeal" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#0d9488" stopOpacity={0.3} />
        <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
      </radialGradient>
      <linearGradient id="railGrad" x1="56" y1="40" x2="56" y2="205" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#0d9488" stopOpacity={0.4} />
        <stop offset="35%" stopColor="#475569" stopOpacity={0.3} />
        <stop offset="65%" stopColor="#475569" stopOpacity={0.3} />
        <stop offset="100%" stopColor="#0d9488" stopOpacity={0.4} />
      </linearGradient>
    </defs>

    {/* ── Gradient dashed rail ── */}
    <line x1="56" y1="40" x2="56" y2="205" stroke="url(#railGrad)" strokeWidth="1.5" strokeDasharray="4 3" />

    {/* ── Node 1: teal + glow ── */}
    <text x="34" y="48" fill="#5eead4" opacity="0.3" style={{ fontSize: 9, fontWeight: 500 }} textAnchor="end">Jan</text>
    <circle cx="56" cy="44" r="22" fill="url(#glowTeal)" />
    <circle cx="56" cy="44" r="6" stroke="#0d9488" strokeWidth="1.5" fill="#0d9488" opacity="0.8" />
    <text x="74" y="48" fill="#5eead4" opacity="0.4" style={{ fontSize: 11, fontWeight: 500 }}>Symptom onset</text>

    {/* ── Node 2: slate open ── */}
    <text x="34" y="102" fill="#64748b" opacity="0.25" style={{ fontSize: 9, fontWeight: 500 }} textAnchor="end">Mar</text>
    <circle cx="56" cy="98" r="6" stroke="#64748b" strokeWidth="1.5" fill="none" opacity="0.45" />
    <text x="74" y="102" fill="#94a3b8" opacity="0.22" style={{ fontSize: 11, fontWeight: 500 }}>Diagnosis</text>

    {/* ── Node 3: slate open ── */}
    <text x="34" y="156" fill="#64748b" opacity="0.25" style={{ fontSize: 9, fontWeight: 500 }} textAnchor="end">Aug</text>
    <circle cx="56" cy="152" r="6" stroke="#64748b" strokeWidth="1.5" fill="none" opacity="0.45" />
    <text x="74" y="156" fill="#94a3b8" opacity="0.22" style={{ fontSize: 11, fontWeight: 500 }}>Conservative therapy</text>

    {/* ── Node 4: teal + glow ── */}
    <text x="34" y="209" fill="#5eead4" opacity="0.3" style={{ fontSize: 9, fontWeight: 500 }} textAnchor="end">Oct</text>
    <circle cx="56" cy="205" r="22" fill="url(#glowTeal)" />
    <circle cx="56" cy="205" r="6" stroke="#0d9488" strokeWidth="1.5" fill="#0d9488" opacity="0.8" />
    <text x="74" y="209" fill="#5eead4" opacity="0.4" style={{ fontSize: 11, fontWeight: 500 }}>Current request</text>
  </svg>
);

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════

type PanelStep = "start" | "ready";

const TimelineBuilder: React.FC<TimelineBuilderProps> = ({
  caseId,
  caseType: initialCaseType = "OTHER",
  caseData,
  initialTimeline,
  onTimelineChange,
  onGenerateOutput,
  readOnly = false,
}) => {
  // ── Core state ─────────────────────────────────────────
  const [events, setEvents] = useState<TimelineEvent[]>(
    initialTimeline?.events || []
  );
  const [anchorDate, setAnchorDate] = useState<string>(
    initialTimeline?.anchor_date || ""
  );
  const [output, setOutput] = useState<TimelineOutput | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  // ── Step flow (simplified: start → ready) ──────────────
  const hasExistingEvents = (initialTimeline?.events || []).length > 0;
  const [panelStep, setPanelStep] = useState<PanelStep>(
    hasExistingEvents ? "ready" : "start"
  );
  const [isBuilding, setIsBuilding] = useState(false);

  // ── Preview panel fade-in ──────────────────────────────
  const [previewVisible, setPreviewVisible] = useState(false);
  useEffect(() => {
    if (panelStep === "start") {
      const timer = setTimeout(() => setPreviewVisible(true), 100);
      return () => clearTimeout(timer);
    }
    setPreviewVisible(false);
  }, [panelStep]);

  // ── Detection + editable case type ─────────────────────
  const detection = useMemo(
    () => detectRequestType(caseData, initialCaseType),
    [caseData, initialCaseType]
  );
  const [selectedCaseType, setSelectedCaseType] = useState<CaseType>(detection.type);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showKeyDates, setShowKeyDates] = useState(false);

  // ── Detection banner state ─────────────────────────────
  const [showDetectionBanner, setShowDetectionBanner] = useState(true);

  const timelineRef = useRef<HTMLDivElement>(null);

  // ── Derived ────────────────────────────────────────────
  const hasCaseData = useMemo(() => {
    if (!caseData) return false;
    return !!(
      caseData.diagnoses?.trim() ||
      (caseData.tried_treatments && caseData.tried_treatments.length > 0) ||
      caseData.denial_reason?.trim() ||
      caseData.request_type?.trim()
    );
  }, [caseData]);

  const validatedEvents = useMemo(
    () => validateTimelineEvents(events),
    [events]
  );

  const sortedEvents = useMemo(
    () => sortTimelineEvents(validatedEvents, anchorDate),
    [validatedEvents, anchorDate]
  );

  const warningCount = sortedEvents.filter((e) => e.hasValidationWarning).length;

  const suggestions = useMemo(
    () => generateSuggestions(events, selectedCaseType),
    [events, selectedCaseType]
  );

  // ── Handlers ───────────────────────────────────────────

  const notifyChange = useCallback(
    (newEvents: TimelineEvent[]) => {
      if (onTimelineChange) {
        onTimelineChange({
          id: initialTimeline?.id || generateEventId(),
          case_id: caseId,
          case_type: selectedCaseType,
          anchor_date: anchorDate,
          events: newEvents,
          created_at: initialTimeline?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    },
    [caseId, selectedCaseType, anchorDate, initialTimeline, onTimelineChange]
  );

  const handleAddEvent = useCallback(() => {
    const newEvent = createEmptyEvent();
    const newEvents = [...events, newEvent];
    setEvents(newEvents);
    notifyChange(newEvents);
    setExpandedEventId(newEvent.id);
  }, [events, notifyChange]);

  const handleUpdateEvent = useCallback(
    (updatedEvent: TimelineEvent) => {
      const newEvents = events.map((e) =>
        e.id === updatedEvent.id ? updatedEvent : e
      );
      setEvents(newEvents);
      notifyChange(newEvents);
    },
    [events, notifyChange]
  );

  const handleDeleteEvent = useCallback(
    (eventId: string) => {
      const newEvents = events.filter((e) => e.id !== eventId);
      setEvents(newEvents);
      notifyChange(newEvents);
      if (expandedEventId === eventId) setExpandedEventId(null);
    },
    [events, notifyChange, expandedEventId]
  );

  const handleDuplicateEvent = useCallback(
    (event: TimelineEvent) => {
      const duplicated: TimelineEvent = {
        ...event,
        id: generateEventId(),
        title: `${event.title} (copy)`,
        isNew: true,
      };
      const newEvents = [...events, duplicated];
      setEvents(newEvents);
      notifyChange(newEvents);
      setExpandedEventId(duplicated.id);
    },
    [events, notifyChange]
  );

  // Primary action — immediately generates
  const handleGenerateTimeline = useCallback(() => {
    setIsBuilding(true);
    setTimeout(() => {
      const templateSuggestions = SUGGESTED_EVENTS_BY_CASE_TYPE[selectedCaseType] || [];
      const templateEvents = createEventsFromSuggestions(templateSuggestions);
      const caseEvents = caseData
        ? createEventsFromCaseData(caseData, selectedCaseType)
        : [];
      const mergedEvents = mergeEventsWithSuggestions(caseEvents, templateEvents);
      setEvents(mergedEvents);
      notifyChange(mergedEvents);
      setIsBuilding(false);
      setPanelStep("ready");
      setTimeout(() => {
        timelineRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }, 600);
  }, [selectedCaseType, caseData, notifyChange]);

  const handleAddFromSuggestion = useCallback(
    (template: EventTemplate) => {
      const newEvent: TimelineEvent = {
        id: generateEventId(),
        event_type: template.event_type,
        date_precision: "approximate",
        title: template.title,
        details: "",
        isNew: true,
      };
      if (template.outcome) {
        newEvent.outcome = template.outcome as TimelineEvent["outcome"];
      }
      const newEvents = [...events, newEvent];
      setEvents(newEvents);
      notifyChange(newEvents);
      setExpandedEventId(newEvent.id);
    },
    [events, notifyChange]
  );

  const handleGenerate = useCallback(async () => {
    if (events.length === 0) return;
    setIsGenerating(true);

    try {
      const payload = {
        caseType: selectedCaseType,
        anchorDate,
        events: sortedEvents.map((e) => {
          // Completion: does this event have substance beyond just a title?
          const hasDate = !!(e.start_date || e.date_label);
          const hasDetails = !!e.details?.trim();
          const hasOutcome = !!(e.outcome && e.outcome !== "PENDING");
          const hasEvidence = !!(e.evidence_flags && e.evidence_flags.length > 0);
          const filledCount = [hasDate, hasDetails, hasOutcome, hasEvidence].filter(Boolean).length;
          const status: "confirmed" | "partial" | "placeholder" =
            filledCount >= 1 ? "confirmed"
            : e.title?.trim() ? "partial"
            : "placeholder";

          return {
            type: e.event_type,
            dateLabel: e.date_label,
            start: e.start_date,
            end: e.end_date,
            title: e.title,
            details: e.details,
            outcome: e.outcome,
            evidenceFlags: e.evidence_flags,
            functionTags: e.function_tags,
            status, // "confirmed" | "partial" | "placeholder"
          };
        }),
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-timeline`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to generate timeline");

      const data = await response.json();
      const generatedOutput: TimelineOutput = {
        narrative: data.narrative,
        bullets: data.bullets,
        proofPoints: data.proofPoints,
        generatedAt: new Date().toISOString(),
        eventsUsed: sortedEvents.length,
        warnings: data.warnings,
      };

      setOutput(generatedOutput);
      onGenerateOutput?.(generatedOutput);
    } catch (error) {
      console.error("Error generating timeline:", error);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedCaseType, anchorDate, sortedEvents, events.length, onGenerateOutput]);

  // ═══════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════

  const isStartState = panelStep === "start" && !readOnly;

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-row"
      style={{
        border: '1px solid #e2e8f0',
        boxShadow: isStartState
          ? '0 2px 12px rgba(0,0,0,0.06), 0 8px 32px rgba(15,23,42,0.08), 0 0 50px rgba(13,148,136,0.1), 0 0 100px rgba(13,148,136,0.06)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04), 0 8px 32px rgba(13,148,136,0.03)',
        background: '#fff',
      }}
    >
  

      {/* ── Main card body ───────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">

        {/* ── Header strip ─────────────────────────────── */}
        <div className="bg-gradient-to-r from-slate-50/80 via-slate-50/30 to-white px-6 py-5 border-b border-slate-200/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-slate-200/60 text-teal-600"
                style={{ boxShadow: '0 1px 3px rgba(13,148,136,0.08), 0 1px 2px rgba(0,0,0,0.04)' }}
              >
                <TimelineIcon />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 leading-tight">
                  Build your case timeline
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Structure events chronologically.
                </p>
              </div>
            </div>


          </div>
        </div>

        {/* ════════════════════════════════════════════════ */}
        {/* STEP: START — M5 Split Hero                     */}
        {/* Light action left  ·  Dark preview right        */}
        {/* ════════════════════════════════════════════════ */}
        {isStartState && (
          <div className="flex flex-row min-h-[260px]">

            {/* ── Left: Light action panel ───────────────── */}
            <div className="flex-1 flex flex-col justify-center px-8 py-10 lg:px-10 lg:py-12">


              {/* Headline */}
              <h4 className="text-lg font-bold text-slate-900 tracking-tight leading-snug">
                Build your timeline automatically
              </h4>

              {/* Subtext */}
              <p className="text-sm text-slate-500 mt-2 leading-relaxed max-w-[300px]">
                AI drafts your case from notes in seconds.
              </p>

              {/* Primary CTA */}
              <div className="mt-8">
                <button
                  onClick={handleGenerateTimeline}
                  disabled={isBuilding}
                  className="
                    inline-flex items-center gap-2.5
                    px-7 py-3.5 rounded-xl
                    text-[14px] font-semibold text-white
                    bg-teal-600
                    hover:bg-teal-700 hover:-translate-y-0.5
                    active:translate-y-0
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none
                    transition-all duration-200
                  "
                  style={{
                    boxShadow: '0 1px 3px rgba(13,148,136,0.3), 0 4px 14px rgba(13,148,136,0.18)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isBuilding) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(13,148,136,0.35), 0 12px 32px rgba(13,148,136,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isBuilding) {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(13,148,136,0.3), 0 4px 14px rgba(13,148,136,0.18)';
                    }
                  }}
                >
                  {isBuilding ? (
                    <>
                      <Spinner className="w-4 h-4" />
                      Generating timeline…
                    </>
                  ) : (
                    <>
                      <SparkIcon className="w-4 h-4" />
                      Generate timeline
                    </>
                  )}
                </button>
              </div>

            </div>

            {/* ── Right: Dark preview panel ──────────────── */}
            <div
              className="hidden sm:flex flex-shrink-0 relative items-center justify-center overflow-hidden"
              style={{
                width: '320px',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderLeft: '1px solid #e2e8f0',
              }}
            >

              {/* Dark timeline illustration */}
              <DarkTimelinePreview visible={previewVisible} />
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════ */}
        {/* STEP: READY — Timeline content                 */}
        {/* ════════════════════════════════════════════════ */}
        {panelStep === "ready" && (
          <div className="px-6 py-6 space-y-5 max-w-2xl mx-auto w-full">

            {/* Suggestion panel */}
            {events.length > 0 && !readOnly && (
              <SuggestionPanel
                suggestions={suggestions}
                onAddEvent={handleAddFromSuggestion}
                defaultCollapsed={true}
              />
            )}

            {/* Timeline events */}
            {sortedEvents.length > 0 && (
              <div ref={timelineRef}>
                {sortedEvents.map((event, index) => (
                  <TimelineEventCard
                    key={event.id}
                    event={{
                      ...event,
                      isNew: expandedEventId === event.id ? true : event.isNew,
                    }}
                    index={index}
                    onUpdate={(updatedEvent) => {
                      handleUpdateEvent(updatedEvent);
                      if (expandedEventId !== event.id && updatedEvent.isNew) {
                        setExpandedEventId(event.id);
                      }
                    }}
                    onDelete={handleDeleteEvent}
                    onDuplicate={handleDuplicateEvent}
                    isFirst={index === 0}
                    isLast={index === sortedEvents.length - 1}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            )}

            {/* Add event button */}
            {!readOnly && events.length > 0 && (
              <button
                onClick={handleAddEvent}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg px-2.5 py-1.5 -ml-1 transition-colors duration-150"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add event
              </button>
            )}

            {/* Build appeal narrative */}
            {events.length > 0 && !readOnly && (
              <div className="pt-2">
                {/* Timeline span summary */}
                {sortedEvents.filter(e => e.start_date || e.date_label).length >= 2 && (
                  <div className="flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg bg-slate-50 border border-slate-100 mb-4">
                    <span className="font-mono text-[11px] font-medium text-slate-400">
                      Timeline span:
                    </span>
                    <span className="font-mono text-[11px] font-semibold text-teal-600">
                      {(() => {
                        const datedEvents = sortedEvents.filter(e => e.start_date);
                        if (datedEvents.length < 2) return "—";
                        const first = new Date(datedEvents[0].start_date!);
                        const last = new Date(datedEvents[datedEvents.length - 1].start_date!);
                        const diffMs = last.getTime() - first.getTime();
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        if (diffDays < 7) return `${diffDays} days`;
                        if (diffDays < 60) return `${Math.round(diffDays / 7)} weeks`;
                        const diffMonths = Math.round(diffDays / 30.44);
                        return `${diffMonths}+ months`;
                      })()}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || events.length === 0}
                  className="
                    w-full inline-flex items-center justify-center gap-2.5
                    px-6 py-3.5 rounded-xl text-sm font-semibold
                    text-white
                    bg-gradient-to-r from-teal-600 to-teal-700
                    hover:from-teal-700 hover:to-teal-800 hover:-translate-y-0.5
                    active:translate-y-0
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2
                    disabled:from-slate-300 disabled:to-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed disabled:hover:translate-y-0
                    transition-all duration-200 group/cta
                  "
                  style={{
                    boxShadow: isGenerating
                      ? 'none'
                      : '0 1px 3px rgba(13,148,136,0.3), 0 4px 14px rgba(13,148,136,0.15)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isGenerating) {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,148,136,0.35), 0 8px 24px rgba(13,148,136,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isGenerating) {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(13,148,136,0.3), 0 4px 14px rgba(13,148,136,0.15)';
                    }
                  }}
                >
                  {isGenerating ? (
                    <>
                      <Spinner className="w-4 h-4" />
                      Building narrative…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 group-hover/cta:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                      </svg>
                      Build Appeal Narrative
                      <svg className="w-3.5 h-3.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </>
                  )}
                </button>
                <p className="text-xs text-slate-400 text-center mt-2">
                  {events.length} event{events.length !== 1 ? "s" : ""} · Produces a structured chronology for peer-to-peer calls
                </p>
              </div>
            )}

            {/* Output */}
            {(output || isGenerating) && (
              <div className="pt-4 border-t border-slate-100">
                <TimelineOutputDisplay
                  output={output}
                  isLoading={isGenerating}
                  caseType={selectedCaseType}
                  onCopy={(text) => console.log("Copied:", text.substring(0, 50))}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineBuilder;